import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    MonthRecurrenceType,
    QuarterMonth,
} from "@common/types/serviceTemplateRecurrence";
import { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import {
    MONTH_OF_YEAR_LABELS,
    WEEKDAY_LABELS,
    dayOfMonthOrdinal,
} from "./serviceTemplateSchedule.constants";

const QUARTER_MONTH_SHORT: Record<QuarterMonth, string> = {
    [QuarterMonth.FIRST]: "1st mo.",
    [QuarterMonth.SECOND]: "2nd mo.",
    [QuarterMonth.THIRD]: "3rd mo.",
};

const WEEKDAY_SHORT: Record<number, string> = {
    0: "Sun",
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
};

function formatWeekly(schedule: ServiceTemplateSchedule): string {
    const { interval, weekDays, weekDay } = schedule;
    const prefix = interval === 1 ? "Weekly" : `Every ${interval} weeks`;
    const days = weekDays && weekDays.length > 0 ? weekDays : [];
    if (days.length === 0 && weekDay !== null) {
        days.push(weekDay);
    }
    if (days.length === 0) {
        return prefix;
    }
    const sorted = [...days].sort((a, b) => a - b);
    const dayLabels = sorted.map(
        (d) => WEEKDAY_SHORT[d] ?? WEEKDAY_LABELS[d] ?? String(d),
    );
    return `${prefix} on ${dayLabels.join(", ")}`;
}

function formatMonthly(schedule: ServiceTemplateSchedule): string {
    const { interval, datesOfMonth, monthRecurrenceType } = schedule;
    const prefix = interval === 1 ? "Monthly" : `Every ${interval} months`;

    if (
        monthRecurrenceType === MonthRecurrenceType.DAY &&
        datesOfMonth &&
        datesOfMonth.length > 0
    ) {
        const sorted = [...datesOfMonth].sort((a, b) => a - b);
        const dayLabels = sorted.map(dayOfMonthOrdinal);
        return `${prefix} on the ${dayLabels.join(", ")}`;
    }

    return prefix;
}

function formatQuarterly(schedule: ServiceTemplateSchedule): string {
    const { interval, quarterMonth, dateOfMonth } = schedule;
    const prefix = interval === 1 ? "Quarterly" : `Every ${interval} quarters`;

    const parts: string[] = [];
    if (quarterMonth !== null && quarterMonth !== undefined) {
        parts.push(QUARTER_MONTH_SHORT[quarterMonth]);
    }
    if (dateOfMonth !== null && dateOfMonth !== undefined) {
        parts.push(dayOfMonthOrdinal(dateOfMonth));
    }

    if (parts.length === 0) {
        return prefix;
    }
    return `${prefix}, ${parts.join(" ")}`;
}

function formatAnnually(schedule: ServiceTemplateSchedule): string {
    const { interval, month, dateOfMonth } = schedule;
    const prefix = interval === 1 ? "Annually" : `Every ${interval} years`;

    const parts: string[] = [];
    if (month !== null && month !== undefined) {
        parts.push(MONTH_OF_YEAR_LABELS[month] ?? String(month));
    }
    if (dateOfMonth !== null && dateOfMonth !== undefined) {
        parts.push(dayOfMonthOrdinal(dateOfMonth));
    }

    if (parts.length === 0) {
        return prefix;
    }
    return `${prefix} on ${parts.join(" ")}`;
}

function formatDaily(schedule: ServiceTemplateSchedule): string {
    const { interval } = schedule;
    return interval === 1 ? "Daily" : `Every ${interval} days`;
}

/**
 * Returns a condensed, human-readable description of how a schedule recurs.
 * Examples:
 *   Daily                         → "Daily"
 *   Every 2 days                  → "Every 2 days"
 *   Weekly on Mon, Wed, Fri       → "Weekly on Mon, Wed, Fri"
 *   Monthly on the 1st, 15th      → "Monthly on the 1st, 15th"
 *   Quarterly, 1st mo. 1st        → "Quarterly, 1st mo. 1st"
 *   Annually on January 1st       → "Annually on January 1st"
 */
export function formatScheduleRecurrence(
    schedule: ServiceTemplateSchedule,
): string {
    switch (schedule.period) {
        case ServiceTemplateRecurrencePeriod.DAILY:
            return formatDaily(schedule);
        case ServiceTemplateRecurrencePeriod.WEEKLY:
            return formatWeekly(schedule);
        case ServiceTemplateRecurrencePeriod.MONTHLY:
            return formatMonthly(schedule);
        case ServiceTemplateRecurrencePeriod.QUARTERLY:
            return formatQuarterly(schedule);
        case ServiceTemplateRecurrencePeriod.ANNUALLY:
            return formatAnnually(schedule);
        default: {
            const exhaustive: never = schedule.period;
            return exhaustive;
        }
    }
}
